<template>
  <div class="flex-y-center gap-3">
    <div
      class="w-[80px] h-[80px] flex items-end justify-center rounded-full border border-gray-400 relative overflow-hidden"
    >
      <img
        v-if="image"
        :src="image"
        alt="profile-avatar"
        class="w-full h-full object-cover"
      />
      <img
        v-else
        src="/images/default-avatar.png"
        alt="profile-avatar"
        class="w-[64px] h-[64px] object-cover cursor-pointer"
      />
      <div
        v-if="image"
        class="bg-dark/[48%] flex-center absolute w-full h-full inset-0 cursor-pointer"
      >
        <i class="icon-edit-contained text-[28px] text-white" />
      </div>
      <Transition name="fade">
        <div
          v-if="loading"
          class="bg-white z-30 absolute inset-0 w-full h-full flex-center"
        >
          <div class="spinner" />
        </div>
      </Transition>
    </div>
  </div>
  <div class="flex-y-center flex-col gap-3 h-auto">
    <div class="relative z-1">
      <CButton
        class="h-9 flex-center text-xs"
        :text="image ? $t('change') : $t('add')"
        variant="info"
        size="lg"
        @click="getFile"
      />
      <input
        id="file-avatar"
        type="file"
        class="w-0 h-0 hidden"
        accept=".jpg, .png, .jpeg, .webp"
        @change="handleChange"
      />
    </div>
    <CButton
      v-if="image"
      class="w-full h-9 flex-center text-xs"
      variant="error"
      :text="$t('delete')"
      @click="removeImage"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import CButton from "@/components/Common/CButton.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { useCustomToast } from "@/composables/useCustomToast";
import { useI18n } from "vue-i18n";

interface Props {
  defaultImage?: string;
}

const emit = defineEmits(["change"]);
const { showToast } = useCustomToast();
const { t } = useI18n();

const props = defineProps<Props>();
const loading = ref(false);

const image = ref<string | undefined>(props.defaultImage);

const authStore = useAuthStore();
const user = computed(() => authStore?.user);

function handleChange(event: any) {
  loading.value = true;
  const target = event?.target as HTMLInputElement | null;
  const file = target?.files[0];
  const reader = new FileReader();
  reader.readAsDataURL(file);
  if (file?.size < 1000000) {
    reader.onload = () => {
      image.value = reader.result;
    };
    emit("change", file);
  } else {
    showToast(t("file_upload_error"), "warning");
  }
  loading.value = false;
}

const getFile = () => {
  const input = document.getElementById("file-avatar");
  input?.click();
};

function removeImage() {
  image.value = "";
  emit("change", "");
}

watch(
  () => props.defaultImage,
  () => {
    if (typeof props.defaultImage === "object") {
      const reader = new FileReader();
      reader.readAsDataURL(props.defaultImage);
      reader.onload = () => {
        image.value = reader.result;
      };
    }
    if (typeof props.defaultImage === "string") {
      image.value = props.defaultImage;
    }
  },
  {
    immediate: true,
  },
);

onMounted(() => {
  if (!image.value) {
    return (image.value = user.value?.avatar_url);
  }
});
</script>

<style scoped>
.spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background:
    radial-gradient(farthest-side, #8968ff 94%, #0000) top/3.8px 3.8px no-repeat,
    conic-gradient(#0000 30%, #8968ff);
  -webkit-mask: radial-gradient(
    farthest-side,
    #0000 calc(100% - 3.8px),
    #000 0
  );
  animation: spinner-c7wet2 1s infinite linear;
}

@keyframes spinner-c7wet2 {
  100% {
    transform: rotate(1turn);
  }
}
</style>
